import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { acceptTeamInvitation } from "@/lib/team-invites.functions";

export const Route = createFileRoute("/team/invite")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search["token"] === "string" ? search["token"] : "",
  }),
  head: () => ({
    meta: [
      { title: "Accept your team invitation — Quibblies" },
      {
        name: "description",
        content: "Redeem a one-time Quibblies team invitation link and activate your staff access.",
      },
      { property: "og:title", content: "Accept your team invitation — Quibblies" },
      { property: "og:description", content: "Redeem a Quibblies team invitation." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TeamInvitePage,
});

const FAILURE_COPY: Record<string, string> = {
  not_found: "This invitation link is not recognised. Ask the Owner to send a new one.",
  revoked: "This invitation was cancelled. Ask the Owner to send a new one.",
  expired: "This invitation has expired. Ask the Owner to send a new one.",
  used: "This invitation has already been used.",
  wrong_account:
    "You are signed in with a different email address. Sign in with the address the invitation was sent to, then open the link again.",
  email_unverified:
    "Confirm your email address first. Open the verification email we sent you, then reload this page.",
};

function TeamInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const accept = useServerFn(acceptTeamInvitation);

  const [signedIn, setSignedIn] = React.useState<boolean | null>(null);
  const [state, setState] = React.useState<"idle" | "working" | "done" | "error">("idle");
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setSignedIn(Boolean(data.user)));
  }, []);

  async function handleAccept() {
    setState("working");
    try {
      const result = await accept({ data: { token } });
      if (result.ok) {
        setState("done");
        toast.success(`You are now a Quibblies ${result.role}`);
        return;
      }
      setState("error");
      setMessage(FAILURE_COPY[result.failure ?? "not_found"] ?? FAILURE_COPY["not_found"]!);
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "Something went wrong");
    }
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-4 py-12">
      <div className="rounded-[2rem] border border-border/70 bg-gradient-card p-7 shadow-lift sm:p-9">
        <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
        <h1 className="mt-3 font-display text-3xl text-foreground">Team invitation</h1>

        {!token ? (
          <p className="mt-3 text-sm text-muted-foreground">
            This page needs the full invitation link from your email.
          </p>
        ) : state === "done" ? (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Your team access is active. Two-factor authentication is required on team accounts —
              set it up before your first shift.
            </p>
            <Button className="mt-5 w-full" onClick={() => void navigate({ to: "/moderation" })}>
              Go to the moderation queue
            </Button>
          </>
        ) : signedIn === false ? (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Sign in first with the email address this invitation was sent to, then come back to
              this link.
            </p>
            <Button asChild className="mt-5 w-full">
              <Link to="/auth">Sign in or create your account</Link>
            </Button>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Accepting adds the role the Owner chose for you. Your email address must be verified.
            </p>
            {state === "error" ? (
              <p className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {message}
              </p>
            ) : null}
            <Button
              className="mt-5 w-full"
              disabled={state === "working" || signedIn === null}
              onClick={handleAccept}
            >
              {state === "working" ? "Accepting…" : "Accept invitation"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
